import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';

const TermsAndPrivacyModal = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
          Terms & Conditions and Privacy Policy
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: '#6b7280' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
            📄 Stratovai Terms & Conditions
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
            <strong>Effective Date:</strong> 08-Dec-2025
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 3, lineHeight: 1.8 }}>
            Welcome to Stratovai! By requesting a demo or using our website, you agree to these Terms & Conditions.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            1. Purpose
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            Stratovai is an AI-powered platform designed to improve product knowledge for QA, Product, and Support teams. Demo access is provided solely for evaluating our solution.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            2. User Responsibilities
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            • You agree to provide accurate contact information when submitting a demo request.<br/>
            • You agree not to misuse, resell, reverse-engineer, or disrupt the services offered.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            3. Demo Access
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            We reserve the right to approve, reject, or revoke demo access at our discretion.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            4. Intellectual Property
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            All content, trademarks, logos, and materials on stratovai.ai are owned by <strong>Kriyanta Labs Pvt Ltd</strong> and protected under intellectual property laws.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            5. Limitation of Liability
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            Stratovai is in a pre-release evaluation phase. We are not liable for any damages or losses arising from use of the demo or this website.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            6. Changes to Terms
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 3, lineHeight: 1.8 }}>
            We may update these Terms occasionally. Continued use of the site implies agreement to the revised Terms.
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#6b7280', fontStyle: 'italic' }}>
            For questions, contact: <a href="mailto:admin@stratovai.ai" style={{ color: '#667eea', fontWeight: 600 }}>admin@stratovai.ai</a>
          </Typography>
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1f2937' }}>
            🔐 Stratovai Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
            <strong>Effective Date:</strong> 08-Dec-2025
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 3, lineHeight: 1.8 }}>
            Your privacy matters to us. This policy explains what we collect, how we use it, and your rights.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            1. Information We Collect
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            When you submit a demo request:<br/>
            • Name, Email, Company, and Message (optional)<br/>
            • IP address and browser metadata<br/>
            • Marketing consent status and timestamp (if given)
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            2. How We Use Your Data
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            • To contact you about the demo<br/>
            • To notify you about updates (only if you opt in)<br/>
            • To improve our product and website experience
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            3. Marketing Communications
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            We only send marketing emails if you have explicitly opted in. You may unsubscribe at any time via the link in our emails or by contacting us.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            4. Data Storage & Security
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            Your data is securely stored in our cloud infrastructure. We do not sell or share your information with third parties.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            5. Your Rights
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            You may:<br/>
            • Request access to your data<br/>
            • Withdraw consent at any time<br/>
            • Request data deletion by emailing <a href="mailto:admin@stratovai.ai" style={{ color: '#667eea', fontWeight: 600 }}>admin@stratovai.ai</a>
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            6. Cookies
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
            We may use cookies to enhance the website experience. You can manage cookie preferences in your browser.
          </Typography>
          
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
            7. Updates to This Policy
          </Typography>
          <Typography variant="body2" sx={{ color: '#374151', mb: 3, lineHeight: 1.8 }}>
            We may revise this policy over time. All updates will be reflected on this page with a new effective date.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#667eea',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              bgcolor: '#5568d3',
            }
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndPrivacyModal;




